import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { formatPhone } from "../../utils/formatPhone";
import { AuthContext } from "../../contexts/AuthContext";

const EditConsumerForm = ({ consumer, onCancel, onUpdated }) => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [states, setStates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showEmploymentFields, setShowEmploymentFields] = useState(false);

  const apiBase = getApiBaseURL();

  // Calculate age from date of birth - helper function
  const calculateAgeFromDOB = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 0 ? age.toString() : "";
  };

  useEffect(() => {
    if (consumer) {
      const dobValue = consumer.date_of_birth
        ? consumer.date_of_birth.substring(0, 10)
        : "";

      // Calculate age from DOB if DOB exists, otherwise use stored age
      const ageValue = dobValue
        ? calculateAgeFromDOB(dobValue)
        : consumer.age || "";

      const data = {
        consumer_id: consumer.consumer_id,
        name: consumer.name || "",
        email: consumer.email || "",
        phone_number: formatPhone(consumer.phone_number || ""),
        counselor_id: consumer.counselor_id || "",
        status_id: consumer.status_id || "",
        referred_by: consumer.referred_by || "",
        case_id: consumer.case_id || "",
        date_of_birth: dobValue,
        notes: consumer.notes || "",
        address_line_1: consumer.address?.address_line_1 || "",
        address_line_2: consumer.address?.address_line_2 || "",
        city: consumer.address?.city || "",
        state_id: consumer.address?.state_id || "",
        postal_code: consumer.address?.postal_code || "",
        emergency_contact_name: consumer.emergency_contact_name || "",
        emergency_contact_phone: formatPhone(
          consumer.emergency_contact_phone || ""
        ),
        // Demographics
        age: ageValue,
        sex: consumer.sex || "",
        marital_status: consumer.marital_status || "",
        // Employment Assessment Fields (CRP-31)
        family_situation: consumer.family_situation || "",
        education: consumer.education || "",
        transportation: consumer.transportation || "",
        work_history: consumer.work_history || "",
        positive_work_traits: consumer.positive_work_traits || "",
        negative_work_traits: consumer.negative_work_traits || "",
        job_preference: consumer.job_preference || "",
        salary_needs: consumer.salary_needs || "",
        benefits_needed: consumer.benefits_needed || "",
        restrictions: consumer.restrictions || "",
      };
      setFormData(data);
      setOriginalData(data);

      // Employment Assessment Data hidden by default
      setShowEmploymentFields(false);
    }
  }, [consumer]);

  useEffect(() => {
    if (!user?.tenant_id) return;

    const fetchData = async () => {
      try {
        const [statesRes, statusesRes, counselorsRes] = await Promise.all([
          secureAxios.get(`${apiBase}/api/states`),
          secureAxios.get(`${apiBase}/api/status`, {
            params: { tenant_id: user.tenant_id },
          }),
          secureAxios.get(`${apiBase}/api/counselors`, {
            params: { tenant_id: user.tenant_id },
          }),
        ]);

        setStates(statesRes.data);

        // Ã¢Å“â€¦ Filter to only show "Account Type" statuses
        const accountTypeStatuses = statusesRes.data.filter(
          (status) => status.status_type_name === "Account Type"
        );
        setStatuses(accountTypeStatuses);

        setCounselors(counselorsRes.data);
      } catch (err) {
        console.error("Failed to fetch form data:", err);
        setError("Failed to load form data.");
      }
    };

    fetchData();
  }, [user?.tenant_id, apiBase]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Auto-calculate age when date of birth changes
    if (name === "date_of_birth") {
      const calculatedAge = calculateAgeFromDOB(value);
      setFormData((prev) => ({
        ...prev,
        date_of_birth: value,
        age: calculatedAge,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          name === "phone_number" || name === "emergency_contact_phone"
            ? formatPhone(value)
            : value,
      }));
    }

    // Clear messages on change
    if (error) setError("");
    if (message) setMessage("");
  };

  const getChangedFields = () => {
    const changes = [];
    const phiFields = [
      "date_of_birth",
      "notes",
      "address_line_1",
      "city",
      "state_id",
      "postal_code",
      "family_situation",
      "work_history",
      "restrictions",
    ];

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== originalData[key]) {
        changes.push({
          field: key,
          oldValue: originalData[key],
          newValue: formData[key],
          isPHI: phiFields.includes(key),
        });
      }
    });

    return changes;
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Consumer name is required");
      return false;
    }

    if (!formData.counselor_id) {
      setError("Counselor selection is required");
      return false;
    }

    if (!formData.status_id) {
      setError("Status selection is required");
      return false;
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (formData.date_of_birth) {
      const dob = new Date(formData.date_of_birth);
      const today = new Date();
      if (dob > today) {
        setError("Date of birth cannot be in the future");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setMessage("Saving changes...");

    // Check if PHI fields were changed for audit purposes
    const changes = getChangedFields();
    const phiChanges = changes.filter((c) => c.isPHI);

    const payload = {
      ...formData,
      phone_number: formData.phone_number.replace(/\D/g, ""),
      emergency_contact_phone: formData.emergency_contact_phone.replace(
        /\D/g,
        ""
      ),
      updated_by: user?.user_id,
      tenant_id: user?.tenant_id,
      update_reason:
        phiChanges.length > 0 ? "PHI fields updated" : "Standard update",
    };

    try {
      await secureAxios.put(
        `${apiBase}/api/consumers/${formData.consumer_id}`,
        payload
      );

      setMessage("Ã¢Å“â€¦ Consumer updated successfully!");

      // Log which fields were changed for audit purposes
      const changedFields = getChangedFields();
      console.log(
        "Ã°Å¸â€œÂ Updated fields:",
        changedFields.map((c) => c.field)
      );

      setTimeout(() => {
        onUpdated();
      }, 1500);
    } catch (err) {
      console.error("Ã¢ÂÅ’ Error updating consumer:", err);
      setError(err.response?.data?.error || "Failed to update consumer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Edit Consumer</h2>
        <span className="badge bg-warning text-dark">
          <i className="bi bi-shield-lock"></i> PHI Edit Form
        </span>
      </div>

      {/* PHI Access Notice */}
      <div className="alert alert-info mb-3" role="alert">
        <i className="bi bi-shield-exclamation"></i>{" "}
        <strong>Accessing Protected Health Information</strong>
        <br />
        You are editing consumer ID: <code>{consumer?.consumer_id}</code>
        <br />
        All changes to PHI fields will be logged with your user ID and timestamp
        for HIPAA compliance.
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {message && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {message}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Basic Information */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Basic Information</h5>
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            name="name"
            className="form-control"
            value={formData.name || ""}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={formData.email || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            name="phone_number"
            className="form-control"
            value={formData.phone_number || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* Demographics Row */}
        <div className="col-md-2">
          <label className="form-label">Sex</label>
          <select
            className="form-select"
            name="sex"
            value={formData.sex || ""}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label">Marital Status</label>
          <select
            className="form-select"
            name="marital_status"
            value={formData.marital_status || ""}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select</option>
            <option value="Single">Single</option>
            <option value="Married">Married</option>
            <option value="Divorced">Divorced</option>
            <option value="Widowed">Widowed</option>
            <option value="Separated">Separated</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label">
            Date of Birth
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </label>
          <input
            type="date"
            name="date_of_birth"
            className="form-control"
            value={formData.date_of_birth || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label">Age</label>
          <input
            className="form-control"
            type="text"
            name="age"
            value={formData.age || ""}
            onChange={handleChange}
            placeholder="â€”"
            disabled={loading || !!formData.date_of_birth}
            readOnly={!!formData.date_of_birth}
            title={
              formData.date_of_birth
                ? "Age is calculated from Date of Birth"
                : "Enter age manually or provide Date of Birth"
            }
          />
          {formData.date_of_birth && (
            <small className="text-muted">Auto-calculated</small>
          )}
        </div>

        <div className="col-md-3">
          <label className="form-label">Case ID</label>
          <input
            name="case_id"
            className="form-control"
            value={formData.case_id || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Status <span className="text-danger">*</span>
          </label>
          <select
            name="status_id"
            className="form-select"
            value={formData.status_id || ""}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Status</option>
            {statuses.map((s) => (
              <option key={s.status_id} value={s.status_id}>
                {s.status}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">
            Counselor <span className="text-danger">*</span>
          </label>
          <select
            name="counselor_id"
            className="form-select"
            value={formData.counselor_id || ""}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Counselor</option>
            {counselors.map((c) => (
              <option key={c.counselor_id} value={c.counselor_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">Referred By</label>
          <input
            name="referred_by"
            className="form-control"
            value={formData.referred_by || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">
            Address Information
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </h5>
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Used for mileage log calculations
          </small>
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            name="address_line_1"
            className="form-control"
            value={formData.address_line_1 || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            name="address_line_2"
            className="form-control"
            value={formData.address_line_2 || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            name="city"
            className="form-control"
            value={formData.city || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            name="state_id"
            className="form-select"
            value={formData.state_id || ""}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.state_id} value={s.state_id}>
                {s.state_code} - {s.state_name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            name="postal_code"
            className="form-control"
            value={formData.postal_code || ""}
            onChange={handleChange}
            maxLength="10"
            disabled={loading}
          />
        </div>

        {/* Emergency Contact */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">Emergency Contact</h5>
        </div>

        <div className="col-md-6">
          <label className="form-label">Emergency Contact Name</label>
          <input
            name="emergency_contact_name"
            className="form-control"
            value={formData.emergency_contact_name || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Emergency Contact Phone</label>
          <input
            type="tel"
            name="emergency_contact_phone"
            className="form-control"
            value={formData.emergency_contact_phone || ""}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* Employment Assessment Data (Collapsible) */}
        <div className="col-12">
          <div className="d-flex align-items-center mt-3 border-bottom pb-2">
            <h5 className="mb-0 me-2">Employment Assessment Data</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowEmploymentFields(!showEmploymentFields)}
            >
              {showEmploymentFields ? (
                <>
                  <i className="bi bi-chevron-up me-1"></i>Hide
                </>
              ) : (
                <>
                  <i className="bi bi-chevron-down me-1"></i>Show
                </>
              )}
            </button>
            <small className="text-muted ms-2">
              (Can also be filled via CRP-31 form)
            </small>
          </div>
        </div>

        {showEmploymentFields && (
          <>
            <div className="col-md-12">
              <label className="form-label">Family Situation</label>
              <textarea
                className="form-control"
                name="family_situation"
                value={formData.family_situation || ""}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Education</label>
              <textarea
                className="form-control"
                name="education"
                value={formData.education || ""}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Transportation</label>
              <textarea
                className="form-control"
                name="transportation"
                value={formData.transportation || ""}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Restrictions (if any)</label>
              <textarea
                className="form-control"
                name="restrictions"
                value={formData.restrictions || ""}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label">Work History</label>
              <textarea
                className="form-control"
                name="work_history"
                value={formData.work_history || ""}
                onChange={handleChange}
                rows="3"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Positive Work Traits</label>
              <textarea
                className="form-control"
                name="positive_work_traits"
                value={formData.positive_work_traits || ""}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">Negative Work Traits</label>
              <textarea
                className="form-control"
                name="negative_work_traits"
                value={formData.negative_work_traits || ""}
                onChange={handleChange}
                rows="2"
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Job Preference</label>
              <input
                className="form-control"
                name="job_preference"
                value={formData.job_preference || ""}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Salary Needs</label>
              <input
                className="form-control"
                name="salary_needs"
                value={formData.salary_needs || ""}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Benefits Needed</label>
              <input
                className="form-control"
                name="benefits_needed"
                value={formData.benefits_needed || ""}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* Notes */}
        <div className="col-12">
          <h5 className="border-bottom pb-2 mt-3">
            Setup Notes
            <i className="bi bi-shield-lock text-warning ms-2" title="PHI"></i>
          </h5>
        </div>

        <div className="col-12">
          <label className="form-label">General Notes</label>
          <textarea
            name="notes"
            className="form-control"
            value={formData.notes || ""}
            onChange={handleChange}
            rows="3"
            disabled={loading}
          />
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Notes may contain Protected Health Information and are encrypted.
          </small>
        </div>

        {/* Submit Buttons */}
        <div className="col-12 d-flex justify-content-between">
          <div>
            <button
              type="submit"
              className="btn btn-primary me-2"
              disabled={loading || !hasChanges()}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Updating...
                </>
              ) : (
                <>
                  <i className="bi bi-save"></i> Update Consumer
                </>
              )}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            {hasChanges() && !loading && (
              <span className="text-muted ms-3">
                <i className="bi bi-info-circle"></i> You have unsaved changes
              </span>
            )}
          </div>
        </div>
      </form>

      {/* Audit Trail */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-clock-history"></i>
          {consumer?.created_at &&
            ` Created: ${new Date(consumer.created_at).toLocaleString()}`}
          {consumer?.updated_at && (
            <>
              <br />
              <i className="bi bi-pencil"></i> Last Updated:{" "}
              {new Date(consumer.updated_at).toLocaleString()}
            </>
          )}
        </small>
      </div>
    </div>
  );
};

export default EditConsumerForm;
